import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// ============ Types ============

export type MfaMethodType = 'totp' | 'email' | 'sms';

export interface MfaMethod {
  type: MfaMethodType;
  factorId?: string; // For TOTP
  destination?: string; // For Email/SMS (masked)
  enrolledAt?: string;
  isActive: boolean;
}

export interface UnifiedMFAStatus {
  hasAnyMfa: boolean;
  isLoading: boolean;
  methods: MfaMethod[];
  preferredMethod: MfaMethodType | null;
}

// ============ Query Keys ============

export const unifiedMfaKeys = {
  all: ['unified-mfa'] as const,
  status: (userId: string) => [...unifiedMfaKeys.all, 'status', userId] as const,
  enrollments: (userId: string) => [...unifiedMfaKeys.all, 'enrollments', userId] as const,
};

// ============ Helper Functions ============

const DEFAULT_METHOD_ORDER: MfaMethodType[] = ['totp', 'email', 'sms'];

export function sortMethodsByPreference(
  methods: MfaMethod[],
  preferredOrder: MfaMethodType[] | null
): MfaMethod[] {
  const order = preferredOrder && preferredOrder.length > 0
    ? preferredOrder as MfaMethodType[]
    : DEFAULT_METHOD_ORDER;
  return [...methods].sort((a, b) => {
    const aIndex = order.indexOf(a.type);
    const bIndex = order.indexOf(b.type);
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  });
}

function maskEmail(email: string): string {
  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) return email;
  const visibleChars = Math.min(3, localPart.length);
  const masked = localPart.slice(0, visibleChars) + '***';
  return `${masked}@${domain}`;
}

function maskPhone(phone: string): string {
  if (phone.length < 6) return phone;
  const lastFour = phone.slice(-4);
  return `***${lastFour}`;
}

// ============ Hooks ============

/**
 * Unified hook to get all MFA methods status for a user
 */
export function useUnifiedMFAStatus(userId: string | undefined) {
  const { data: totpFactors, isLoading: totpLoading } = useQuery({
    queryKey: ['mfa-factors'],
    queryFn: async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      return data?.totp || [];
    },
    enabled: !!userId,
  });

  const { data: otpEnrollments, isLoading: otpLoading } = useQuery({
    queryKey: unifiedMfaKeys.enrollments(userId ?? ''),
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from('otp_mfa_enrollments')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true);
      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
  });

  const { data: mfaStatus } = useQuery({
    queryKey: ['mfa-status', userId ?? ''],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from('mfa_enrollment_status')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  const methods: MfaMethod[] = [];

  // Add TOTP factors
  if (totpFactors) {
    for (const factor of totpFactors) {
      if (factor.status === 'verified') {
        methods.push({
          type: 'totp',
          factorId: factor.id,
          enrolledAt: factor.created_at,
          isActive: true,
        });
      }
    }
  }

  // Add Email/SMS enrollments
  if (otpEnrollments) {
    for (const enrollment of otpEnrollments) {
      methods.push({
        type: enrollment.method as MfaMethodType,
        destination: enrollment.method === 'email' 
          ? maskEmail(enrollment.destination)
          : maskPhone(enrollment.destination),
        enrolledAt: enrollment.created_at,
        isActive: enrollment.is_active,
      });
    }
  }

  // Sort methods by user preference
  const methodOrder = (mfaStatus?.preferred_method_order as MfaMethodType[] | null) ?? null;
  const sortedMethods = sortMethodsByPreference(methods, methodOrder);
  const preferredMethod = sortedMethods.length > 0 ? sortedMethods[0].type : null;

  return {
    hasAnyMfa: sortedMethods.length > 0,
    isLoading: totpLoading || otpLoading,
    methods: sortedMethods,
    preferredMethod,
    mfaStatus,
    methodOrder,
  };
}

/**
 * Hook to check if MFA is required after login
 * Works with Supabase's native AAL check + custom OTP enrollments
 */
export function useMFARequiredCheck() {
  const { _t } = useTranslation('common');

  return useMutation({
    mutationFn: async (userId: string): Promise<{
      required: boolean;
      methods: MfaMethod[];
      preferredMethod: MfaMethodType | null;
    }> => {
      // Check Supabase's native MFA level
      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      
      // Get TOTP factors
      const { data: factorData } = await supabase.auth.mfa.listFactors();
      const totpFactors = factorData?.totp || [];
      const verifiedTotp = totpFactors.filter(f => f.status === 'verified');

      // Get OTP enrollments
      const { data: otpEnrollments } = await supabase
        .from('otp_mfa_enrollments')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true);

      const methods: MfaMethod[] = [];

      // Add TOTP
      for (const factor of verifiedTotp) {
        methods.push({
          type: 'totp',
          factorId: factor.id,
          isActive: true,
        });
      }

      // Add Email/SMS
      for (const enrollment of otpEnrollments || []) {
        methods.push({
          type: enrollment.method as MfaMethodType,
          destination: enrollment.method === 'email'
            ? maskEmail(enrollment.destination)
            : maskPhone(enrollment.destination),
          isActive: true,
        });
      }

      // Fetch user's preferred method order
      const { data: mfaPrefs } = await supabase
        .from('mfa_enrollment_status')
        .select('preferred_method_order')
        .eq('user_id', userId)
        .maybeSingle();

      // MFA is required if:
      // 1. Supabase says we need AAL2 (for TOTP)
      // 2. OR we have any OTP enrollments
      const needsNativeAal2 = aalData?.currentLevel === 'aal1' && aalData?.nextLevel === 'aal2';
      const hasOtpEnrollments = (otpEnrollments || []).length > 0;

      const required = needsNativeAal2 || hasOtpEnrollments || methods.length > 0;
      const sortedMethods = sortMethodsByPreference(
        methods,
        mfaPrefs?.preferred_method_order as MfaMethodType[] | null
      );
      const preferredMethod = sortedMethods.length > 0 ? sortedMethods[0].type : null;

      return { required, methods: sortedMethods, preferredMethod };
    },
  });
}

/**
 * Unified hook to verify MFA code for any method
 */
export function useUnifiedMFAVerify() {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      method,
      code,
      factorId,
    }: {
      method: MfaMethodType;
      code: string;
      factorId?: string;
    }): Promise<{ success: boolean }> => {
      if (method === 'totp') {
        // Use Supabase's native TOTP verification
        if (!factorId) throw new Error('Factor ID required for TOTP');
        
        const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
          factorId,
        });
        if (challengeError) throw challengeError;

        const { error: verifyError } = await supabase.auth.mfa.verify({
          factorId,
          challengeId: challengeData.id,
          code,
        });
        if (verifyError) throw verifyError;

        // Update last verified timestamp
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('mfa_enrollment_status')
            .update({ last_verified_at: new Date().toISOString() })
            .eq('user_id', user.id);

          await supabase.rpc('log_session_event', {
            p_user_id: user.id,
            p_event_type: 'mfa_success',
            p_metadata: { method: 'totp' },
          });
        }
      } else {
        // Use custom OTP verification via edge function
        const { data, error } = await supabase.functions.invoke('verify-mfa-otp', {
          body: { method, code },
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: unifiedMfaKeys.all });
      toast.success(t('mfa.verifySuccess', 'Verification successful'));
    },
    onError: (error: Error) => {
      console.error('MFA verification error:', error);
      const message = error.message?.includes('expired')
        ? t('mfa.otpExpired', 'Code expired. Please request a new one.')
        : t('mfa.invalidCode', 'Invalid verification code');
      toast.error(message);
    },
  });
}

/**
 * Hook to send OTP for MFA challenge (Email or SMS)
 */
export function useSendMFAChallenge() {
  const { t } = useTranslation('common');

  return useMutation({
    mutationFn: async ({ method }: { method: 'email' | 'sms' }) => {
      const { data, error } = await supabase.functions.invoke('send-mfa-otp', {
        body: { method },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return data;
    },
    onSuccess: (_, { method }) => {
      const methodName = method === 'email' ? 'email' : 'phone';
      toast.success(t('mfa.codeSent', { methodName, defaultValue: 'Verification code sent to your {{methodName}}' }));
    },
    onError: (error: Error) => {
      console.error('Send MFA challenge error:', error);
      toast.error(t('mfa.sendError', 'Failed to send verification code'));
    },
  });
}

/**
 * Hook to unenroll from a specific MFA method
 */
export function useUnenrollMFAMethod() {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ method, factorId }: { method: MfaMethodType; factorId?: string }) => {
      if (method === 'totp') {
        if (!factorId) throw new Error('Factor ID required for TOTP unenroll');
        const { error } = await supabase.auth.mfa.unenroll({ factorId });
        if (error) throw error;

        // Update enrollment status
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Check if user has other MFA methods
          const { data: otpEnrollments } = await supabase
            .from('otp_mfa_enrollments')
            .select('id')
            .eq('user_id', user.id)
            .eq('is_active', true);

          const hasOtherMethods = (otpEnrollments || []).length > 0;

          if (!hasOtherMethods) {
            await supabase
              .from('mfa_enrollment_status')
              .update({ is_enrolled: false })
              .eq('user_id', user.id);
          }
        }
      } else {
        // Unenroll OTP method via edge function
        const { data, error } = await supabase.functions.invoke('unenroll-otp-mfa', {
          body: { method },
        });

        if (error) throw error;
        if (data?.error) throw new Error(data.error);
      }

      return { method };
    },
    onSuccess: ({ method }) => {
      queryClient.invalidateQueries({ queryKey: unifiedMfaKeys.all });
      queryClient.invalidateQueries({ queryKey: ['mfa-factors'] });
      queryClient.invalidateQueries({ queryKey: ['mfa-status'] });

      const methodName = method === 'totp' 
        ? t('mfa.method.totp', 'Authenticator App')
        : method === 'email'
          ? t('mfa.method.email', 'Email')
          : t('mfa.method.sms', 'SMS');
      toast.success(t('mfa.unenrollSuccess', `${methodName} removed successfully`));
    },
    onError: (error: Error) => {
      console.error('MFA unenroll error:', error);
      toast.error(t('mfa.unenrollError', 'Failed to remove verification method'));
    },
  });
}

/**
 * Hook to update the user's preferred MFA method order
 */
export function useUpdateMFAMethodOrder() {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      methodOrder,
    }: {
      userId: string;
      methodOrder: MfaMethodType[];
    }) => {
      const { error } = await supabase
        .from('mfa_enrollment_status')
        .update({ preferred_method_order: methodOrder })
        .eq('user_id', userId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: unifiedMfaKeys.all });
      queryClient.invalidateQueries({ queryKey: ['mfa-status'] });
      toast.success(t('security.mfa.orderSaved', 'Method order updated'));
    },
    onError: () => {
      toast.error(t('security.mfa.orderError', 'Failed to update method order'));
    },
  });
}
